/**
 * The dedicated DB's SQL Server controls of step 3, styled like Azure's "SQL virtual machine" blade
 * and only with the classes of NewDeploymentPage.module.css:
 *
 *  - SqlImageField: the one image offered, read-only (SQL Server 2022 Standard on Windows Server 2022).
 *  - SqlAuthenticationFields: Windows authentication, or SQL Server authentication with a required
 *    login (one or the other). No password input: the pipeline generates it and stores it in Key Vault.
 *  - SqlStoragePanel: "Configure SQL Server storage": the workload and the Data, Log and TempDB rows
 *    (disk type, size per disk, number of disks; TempDB first picks the local SSD or dedicated disks),
 *    with a read-only summary of the volumes and the total disk count.
 */
import { useEffect, useRef } from 'react'
import clsx from 'clsx'
import { useFormContext, useWatch } from 'react-hook-form'
import {
  DB_IMAGE_LABEL,
  DB_ROLE_TITLES,
  DB_VOLUMES,
  DISK_SIZE_MAX_GB,
  DISK_SIZE_MIN_GB,
  DISK_TYPES,
  MAX_DISKS,
  SQL_AUTH_OPTIONS,
  STORAGE_WORKLOAD_OPTIONS,
  TEMPDB_LOCATION_OPTIONS,
} from '@/config/workwise'
import styles from '@/pages/NewDeploymentPage.module.css'
import type { DbDiskRole, WorkwiseFormValues } from '@/types/workwise'
import { DB_STORAGE_PANEL_PATH, dbDiskTotalText, dbVolumeSummary } from '@/utils/workwise/dbStorage'
import { errorAt, fieldId } from './fieldHelpers'
import { InfoField, SelectField, TextField } from './fields'

/** The pipeline-generated SQL password: where it ends up, said in the login field's hint. */
const SQL_PASSWORD_NOTE =
  'No password to enter: it is generated during deployment and stored in Key Vault as <vm name>-sql-password.'

// ─── SQL image (read-only) ───────────────────────────────────────────────────

/** The only image a dedicated DB offers; a backend error at dbServer.engineVersion is shown under it. */
export function SqlImageField() {
  const {
    formState: { errors },
  } = useFormContext<WorkwiseFormValues>()
  return (
    <InfoField
      label="SQL Server image"
      value={DB_IMAGE_LABEL}
      hint="The only image offered"
      testId="ww-db-image"
      error={errorAt(errors, 'dbServer.engineVersion')}
      errorTestId="error-dbServer.engineVersion"
    />
  )
}

// ─── SQL authentication ──────────────────────────────────────────────────────

/** Two cells of the DB form grid: the authentication choice and, for SQL Server authentication, the SQL login name. */
export function SqlAuthenticationFields() {
  const { control, clearErrors } = useFormContext<WorkwiseFormValues>()
  const mode = useWatch({ control, name: 'dbServer.sqlAuthentication' })
  return (
    <>
      <SelectField
        path="dbServer.sqlAuthentication"
        label="SQL authentication"
        options={SQL_AUTH_OPTIONS}
        testId="ww-db-sql-auth"
        // The login is neither shown nor sent for Windows authentication, so its error must not linger.
        onChange={(value) => {
          if (value !== 'sql') clearErrors('dbServer.sqlLogin')
        }}
      />
      {mode === 'sql' && (
        <TextField
          path="dbServer.sqlLogin"
          label="SQL login name"
          required
          maxLength={32}
          placeholder="e.g. workwise_sql"
          testId="ww-db-sql-login"
          hint={<span data-testid="ww-db-sql-login-hint">{SQL_PASSWORD_NOTE}</span>}
        />
      )}
    </>
  )
}

// ─── SQL Server storage ──────────────────────────────────────────────────────

/** Disk type, size and count side by side. */
const CONTROL_GRID = { display: 'grid', gridTemplateColumns: 'minmax(150px, 1.4fr) minmax(110px, 1fr) minmax(110px, 1fr)', gap: 12 } as const
/** One row of the panel (Data, Log, TempDB) in a bordered block. */
const ROW_BOX = {
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  padding: '12px 14px',
  border: '1px solid var(--aptean-border)',
  borderRadius: 'var(--aptean-radius)',
} as const

interface RowTitleProps {
  title: string
  volume: string
  testId: string
}

function RowTitle({ title, volume, testId }: RowTitleProps) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
      <span className={styles.label}>{title}</span>
      <span className={styles.vnetHint} data-testid={testId}>
        {volume}
      </span>
    </div>
  )
}

interface GroupControlsProps {
  role: DbDiskRole
  /** The total number of disks is over the cap: the count boxes are the controls to fix. */
  overTotal: boolean
}

/** Disk type, Size per disk (GB) and Number of disks of one row. */
function GroupControls({ role, overTotal }: GroupControlsProps) {
  const {
    register,
    formState: { errors },
  } = useFormContext<WorkwiseFormValues>()
  const typePath = `dbServer.storage.${role}.type` as const
  const sizePath = `dbServer.storage.${role}.sizeGb` as const
  const countPath = `dbServer.storage.${role}.count` as const
  const typeError = errorAt(errors, typePath)
  const sizeError = errorAt(errors, sizePath)
  const countError = errorAt(errors, countPath)
  const countInvalid = Boolean(countError) || overTotal
  const message = (path: string, text: string | undefined) =>
    text ? (
      <span className={styles.errorMsg} role="alert" data-testid={`error-${path}`}>
        {text}
      </span>
    ) : null
  return (
    <div style={CONTROL_GRID}>
      <div className={styles.formGroup}>
        <label className={styles.label} htmlFor={fieldId(typePath)}>
          Disk type
        </label>
        <select
          id={fieldId(typePath)}
          data-testid={`ww-db-${role}-type`}
          className={clsx(styles.select, typeError && styles.inputError)}
          aria-invalid={typeError ? true : undefined}
          {...register(typePath)}
        >
          {DISK_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        {message(typePath, typeError)}
      </div>
      <div className={styles.formGroup}>
        <label className={styles.label} htmlFor={fieldId(sizePath)}>
          Size per disk (GB)
        </label>
        <input
          id={fieldId(sizePath)}
          type="number"
          min={DISK_SIZE_MIN_GB}
          max={DISK_SIZE_MAX_GB}
          data-testid={`ww-db-${role}-size`}
          className={clsx(styles.input, sizeError && styles.inputError)}
          aria-invalid={sizeError ? true : undefined}
          autoComplete="off"
          {...register(sizePath, { valueAsNumber: true })}
        />
        {message(sizePath, sizeError)}
      </div>
      <div className={styles.formGroup}>
        <label className={styles.label} htmlFor={fieldId(countPath)}>
          Number of disks
        </label>
        <input
          id={fieldId(countPath)}
          type="number"
          min={1}
          max={MAX_DISKS}
          data-testid={`ww-db-${role}-count`}
          className={clsx(styles.input, countInvalid && styles.inputError)}
          aria-invalid={countInvalid ? true : undefined}
          autoComplete="off"
          {...register(countPath, { valueAsNumber: true })}
        />
        {message(countPath, countError)}
      </div>
    </div>
  )
}

function TempDbRow({ overTotal }: { overTotal: boolean }) {
  const { control, clearErrors } = useFormContext<WorkwiseFormValues>()
  const location = useWatch({ control, name: 'dbServer.storage.tempdb.location' })
  const onDisks = location === 'disks'
  return (
    <div style={ROW_BOX} data-testid="ww-db-tempdb-row">
      <RowTitle title={DB_ROLE_TITLES.tempdb} volume={onDisks ? DB_VOLUMES.tempdb : `${DB_VOLUMES.tempdbLocal} (local)`} testId="ww-db-tempdb-volume" />
      <SelectField
        path="dbServer.storage.tempdb.location"
        label="TempDB location"
        options={TEMPDB_LOCATION_OPTIONS}
        testId="ww-db-tempdb-location"
        // Local SSD has no disks: the type, size and count errors of dedicated disks no longer apply.
        onChange={(value) => {
          if (value !== 'disks') {
            clearErrors(['dbServer.storage.tempdb.type', 'dbServer.storage.tempdb.sizeGb', 'dbServer.storage.tempdb.count'])
          }
        }}
      />
      {onDisks && <GroupControls role="tempdb" overTotal={overTotal} />}
    </div>
  )
}

/** "SQL Server storage": the workload, the Data / Log / TempDB rows, and what will be created. */
export function SqlStoragePanel() {
  const {
    control,
    trigger,
    formState: { errors },
  } = useFormContext<WorkwiseFormValues>()
  const [storage, zone] = useWatch({ control, name: ['dbServer.storage', 'dbServer.zone'] })
  const totalError = errorAt(errors, DB_STORAGE_PANEL_PATH)
  const zoneError = errorAt(errors, 'dbServer.zone')

  // The total and the zone rule depend on other fields: once one of them is flagged, re-check it as the
  // storage or the zone changes, so a fixed problem clears without another click on Next. Mounting the
  // panel (e.g. coming back to step 3 after a server-side error) re-checks nothing: those errors stay
  // until a value changes.
  const changeKey = JSON.stringify([storage, zone])
  const checkedKey = useRef(changeKey)
  const flagged = Boolean(totalError || zoneError)
  useEffect(() => {
    if (checkedKey.current === changeKey) return
    checkedKey.current = changeKey
    if (flagged) void trigger(['dbServer.zone', 'dbServer.storage'])
  }, [changeKey, flagged, trigger])

  return (
    <div className={styles.formGroup} style={{ marginTop: 16 }} data-testid="ww-db-storage">
      <label className={styles.label}>SQL Server storage</label>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <SelectField
          path="dbServer.storageWorkload"
          label="Storage optimized for"
          options={STORAGE_WORKLOAD_OPTIONS}
          testId="ww-db-workload"
        />
        <div style={ROW_BOX} data-testid="ww-db-data-row">
          <RowTitle title={DB_ROLE_TITLES.data} volume={DB_VOLUMES.data} testId="ww-db-data-volume" />
          <GroupControls role="data" overTotal={Boolean(totalError)} />
        </div>
        <div style={ROW_BOX} data-testid="ww-db-log-row">
          <RowTitle title={DB_ROLE_TITLES.log} volume={DB_VOLUMES.log} testId="ww-db-log-volume" />
          <GroupControls role="log" overTotal={Boolean(totalError)} />
        </div>
        <TempDbRow overTotal={Boolean(totalError)} />
        <div className={styles.vnetInfoBox} data-testid="ww-db-storage-summary">
          <span className={styles.vnetName} data-testid="ww-db-storage-volumes" style={{ wordBreak: 'break-word' }}>
            {dbVolumeSummary(storage)}
          </span>
          <span className={styles.vnetHint} data-testid="ww-db-storage-total">
            {dbDiskTotalText(storage)}
          </span>
        </div>
        {totalError && (
          <span className={styles.errorMsg} role="alert" data-testid={`error-${DB_STORAGE_PANEL_PATH}`}>
            {totalError}
          </span>
        )}
      </div>
    </div>
  )
}
